import React, { useState } from 'react';
import {
  Button,
  ContextMenu,
  ContextMenuItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  useApi,
} from '@axiom/ui';

import { TagType } from '../../enums/generated/tag-type';
import { LegacyTagsApi } from '../../api/legacy-tags-api';
import type { Tag } from '../../types/generated/tag';

import { AdminModalSearchListDetail } from './AdminModalSearchListDetail';
import { AdminModalSearchList } from './AdminModalSearchList';
import { AdminModalDetail } from './AdminModalDetail';
import { AdminModalTagsForm } from './AdminModalTagsForm';

export const AdminModalTagsView = () => {
  const [{ data: allTags }] = useApi(LegacyTagsApi.readTags());
  const tags = allTags.filter(tag => tag.type === TagType.Tag);
  const [selected, setSelected] = useState<Partial<Tag> | null>(null);
  const [search, setSearch] = useState('');
  const [stagedForDeletion, setStagedForDeletion] = useState<Tag | null>(null);

  const visible = search
    ? tags.filter(tag => tag.name?.toLowerCase().includes(search.toLowerCase()))
    : tags;

  const handleDelete = async (tagId: Tag['id']) => {
    await LegacyTagsApi.deleteTag({ id: tagId });
    setSelected(null);
  };

  return (
    <>
      <AdminModalSearchListDetail
        name="MANAGE_TAGS"
        list={
          <AdminModalSearchList<Tag>
            name="TAG_LIST"
            searchName="TAG_SEARCH_INPUT"
            onSearchChange={setSearch}
            items={visible}
            selectedId={selected?.id ?? null}
            onSelect={setSelected}
            addControl={{
              kind: 'button',
              label: 'Create New Tag',
              name: 'CREATE_TAG_BUTTON',
              onAdd: () => {
                setSelected({});
              },
            }}
            renderRow={tag => ({
              content: <span data-test="TAG_LABEL">{tag.name}</span>,
              trailing: (
                <ContextMenu direction="left">
                  <ContextMenuItem
                    name="DELETE_TAG"
                    onClick={() => {
                      setStagedForDeletion(tag);
                    }}
                  >
                    Delete Tag
                  </ContextMenuItem>
                </ContextMenu>
              ),
            })}
          />
        }
        detail={
          <AdminModalDetail
            emptyText="Select a tag."
            emptyName="NO_TAG_SELECTED"
          >
            {selected ? (
              <AdminModalTagsForm
                key={selected.id ?? null}
                name="TAG_FORM"
                tags={tags}
                selected={selected}
                onClose={() => {
                  setSelected(null);
                }}
              />
            ) : null}
          </AdminModalDetail>
        }
      />
      {stagedForDeletion ? (
        <Modal size="confirm">
          <ModalHeader
            onClose={() => {
              setStagedForDeletion(null);
            }}
          >
            Delete Tag
          </ModalHeader>
          <ModalSection>
            <Paragraph>
              You have selected to delete this tag. Doing so will remove it from
              all jobs, talent profiles and saved searches where this has been
              selected.
            </Paragraph>
          </ModalSection>
          <ModalFooter>
            <Button
              variation="outline"
              onClick={() => {
                setStagedForDeletion(null);
              }}
            >
              Cancel
            </Button>
            <Button
              name="CONFIRM_DELETE_BUTTON"
              onClick={() => {
                void handleDelete(stagedForDeletion.id);
                setStagedForDeletion(null);
              }}
            >
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
};
